/**
 * DlgAdvisoryBadge - Shows DLG state inline on OptiPlace ranking cards
 * 
 * Phase 3: Diagnostic Load Guard (DLG) Frontend
 */

import { Badge } from '@/components/ui/badge';
import { Shield, ShieldAlert, ShieldCheck } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export type DlgState = 'clear' | 'elevated' | 'saturated';

interface DlgAdvisoryBadgeProps {
  state: DlgState;
  explanation?: string;
}

const DLG_CONFIG: Record<DlgState, {
  icon: typeof Shield;
  label: string;
  className: string;
  dotColor: string;
}> = {
  clear: {
    icon: ShieldCheck,
    label: 'Well positioned',
    className: 'border-green-500/40 bg-green-500/10 text-green-700 dark:text-green-400',
    dotColor: 'bg-green-500',
  },
  elevated: {
    icon: Shield,
    label: 'Heavy load',
    className: 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400',
    dotColor: 'bg-amber-500',
  },
  saturated: {
    icon: ShieldAlert,
    label: 'May need a break',
    className: 'border-orange-500/40 bg-orange-500/10 text-orange-700 dark:text-orange-400',
    dotColor: 'bg-orange-500',
  },
};

export function DlgAdvisoryBadge({ state, explanation }: DlgAdvisoryBadgeProps) {
  const config = DLG_CONFIG[state];
  const Icon = config.icon;

  const badge = (
    <Badge variant="outline" className={`text-[10px] gap-1 ${config.className}`}>
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${config.dotColor}`} />
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );

  if (!explanation) return badge;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          {explanation}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
